import { WarningFilled } from '@ant-design/icons';
import { Dropdown, Flex } from 'antd';
import cn from 'classnames';

import type { TimeFormat } from '@app/api/Branches/types';
import { Avatar } from '@app/components/Avatar';

import type {
  AppointmentPreviewField,
  CalendarBreak,
  CalendarEvent,
  CalendarSlotMenu,
  Employee,
} from '@pages/Calendar/data';

import { AppointmentCard } from '../../AppointmentCard';
import { useEmployeeColumn } from '../hooks';

import type { EventDragState } from '../../AppointmentCard/hooks';
import type { FC } from 'react';

import '../styles/EmployeeColumn.scss';

interface EmployeeColumnProps {
  employee: Employee;
  employees: Employee[];
  events: CalendarEvent[];
  startTime: string;
  endTime: string;
  stepMinutes: number;
  timeFormat?: TimeFormat | null;
  intervalHeightPx: number;
  showHeader?: boolean;
  onSlotClick: (employeeId: string, time: string, endTime: string) => void;
  onSlotBreakClick?: (
    employeeId: string,
    time: string,
    endTime: string
  ) => void;
  onBreakClick?: (employeeId: string, calendarBreak: CalendarBreak) => void;
  onEventUpdate: (
    eventId: string,
    newStart: string,
    newEnd: string,
    newEmployeeId?: string
  ) => void;
  onDragStateChange?: (state: EventDragState | null) => void;
  onRecordClick: (event: CalendarEvent) => void;
  canShowContacts: boolean;
  previewFields: AppointmentPreviewField[];
  activeSlotMenu: CalendarSlotMenu | null;
  onSlotMenuOpen: (slotMenu: CalendarSlotMenu) => void;
  onSlotMenuClose: () => void;
}

export const EmployeeColumn: FC<EmployeeColumnProps> = ({
  employee,
  employees,
  events,
  startTime,
  endTime,
  stepMinutes,
  timeFormat,
  intervalHeightPx,
  showHeader = true,
  onSlotClick,
  onSlotBreakClick,
  onBreakClick,
  onEventUpdate,
  onDragStateChange,
  onRecordClick,
  canShowContacts,
  previewFields,
  activeSlotMenu,
  onSlotMenuOpen,
  onSlotMenuClose,
}) => {
  const {
    timeSlots,
    layoutedEvents,
    breakBlocks,
    activeEventId,
    gridHoverHandlers,
    handleBreakClick,
    handleSlotClick,
    slotMenu,
    slotMenuItems,
    closeSlotMenu,
  } = useEmployeeColumn({
    ...employee,
    startTime,
    endTime,
    stepMinutes,
    timeFormat,
    intervalHeightPx,
    events,
    onSlotClick,
    onSlotBreakClick,
    onBreakClick,
    activeSlotMenu,
    onSlotMenuOpen,
    onSlotMenuClose,
  });

  const { id, isWorkdayDeleted, name, position } = employee;
  const isArchived = isWorkdayDeleted === true;

  return (
    <div
      className={cn('EmployeeColumn', {
        'EmployeeColumn--archived': isArchived,
      })}
      data-testid={`employee-column-${id}`}
    >
      {showHeader && (
        <div className="EmployeeColumn__header">
          <Avatar
            src={employee.avatarUrl}
            nameFirst={employee.firstName ?? name}
            nameLast={employee.lastName}
            size={24}
            className="EmployeeColumn__avatar"
          />
          <span className="EmployeeColumn__name">{name}</span>
          {!!position && (
            <span className="EmployeeColumn__position">{position}</span>
          )}
        </div>
      )}

      <div className="EmployeeColumn__grid" {...gridHoverHandlers}>
        {timeSlots.map((slot) => {
          const {
            time,
            label,
            isOccupied,
            isNonWorking,
            isNonWorkingTimeSection,
            isBreak,
            isInactiveWorkday,
            isBeforeBlock,
            isBlockStart,
            isBlockEnd,
            shouldShowNonWorkingTimeLabel,
          } = slot;

          return (
            <div
              key={time}
              className={cn('EmployeeColumn__slot', {
                'EmployeeColumn__slot--occupied': isOccupied,
                'EmployeeColumn__slot--non-working': isNonWorking,
                'EmployeeColumn__slot--non-working-time':
                  isNonWorkingTimeSection,
                'EmployeeColumn__slot--break': isBreak,
                'EmployeeColumn__slot--inactive-workday': isInactiveWorkday,
                'EmployeeColumn__slot--before-block': isBeforeBlock,
                'EmployeeColumn__slot--block-start': isBlockStart,
                'EmployeeColumn__slot--block-end': isBlockEnd,
              })}
              data-testid={`employee-slot-${id}-${time}`}
              onClick={
                isBreak || isOccupied || isInactiveWorkday
                  ? undefined
                  : (event) => handleSlotClick(slot, event)
              }
            >
              {!isOccupied && !isBreak && !isInactiveWorkday && (
                <div
                  className={cn('EmployeeColumn__slot-hover', {
                    'EmployeeColumn__slot-hover--non-working-time':
                      isNonWorkingTimeSection,
                    'EmployeeColumn__slot-hover--with-warning-label':
                      shouldShowNonWorkingTimeLabel,
                  })}
                >
                  <Flex gap={4} align="center">
                    <span className="EmployeeColumn__slot-time">{label}</span>
                    <span className="EmployeeColumn__slot-label">
                      Новый сеанс
                    </span>
                  </Flex>
                  {isNonWorkingTimeSection && shouldShowNonWorkingTimeLabel && (
                    <span className="EmployeeColumn__slot-warning-label">
                      <WarningFilled className="EmployeeColumn__slot-warning-icon" />
                      Нерабочее время
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* key по шагу — меню заново встаёт у нового шага. trigger click —
            rc-trigger закрывает попап по клику мимо него только при click;
            якорь с pointer-events: none, клик по шагу доходит до слота. */}
        {slotMenu && (
          <Dropdown
            key={slotMenu.time}
            menu={{ items: slotMenuItems }}
            open
            trigger={['click']}
            placement="bottomLeft"
            onOpenChange={(isOpen) => {
              if (!isOpen) closeSlotMenu();
            }}
          >
            <div
              className="EmployeeColumn__slot-menu-anchor"
              style={{ top: slotMenu.topPx, height: slotMenu.heightPx }}
              data-testid={`employee-slot-menu-${id}-${slotMenu.time}`}
            />
          </Dropdown>
        )}

        {breakBlocks.map(({ calendarBreak, topPx, heightPx }) => (
          <div
            key={calendarBreak.id}
            className="EmployeeColumn__break-block"
            style={{ top: topPx, height: heightPx }}
            data-testid={`employee-break-${id}-${calendarBreak.id}`}
            onClick={(event) => handleBreakClick(event, calendarBreak)}
          >
            <span className="EmployeeColumn__break-label">Перерыв</span>
          </div>
        ))}

        {layoutedEvents.map(({ event, layout }) => (
          <AppointmentCard
            key={event.id}
            event={event}
            employees={employees}
            layout={layout}
            isActive={activeEventId === event.id}
            calendarStartTime={startTime}
            calendarEndTime={endTime}
            stepMinutes={stepMinutes}
            intervalHeightPx={intervalHeightPx}
            onEventUpdate={onEventUpdate}
            onDragStateChange={onDragStateChange}
            onRecordClick={onRecordClick}
            canShowContacts={canShowContacts}
            previewFields={previewFields}
          />
        ))}
      </div>
    </div>
  );
};
